<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

// 导入背景图片
import assistantsHeaderImg from 'assets/images/dashboard/pilot/assistants-header.png';

const props = defineProps({
  assistant: {
    type: Object,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  showMenu: {
    type: Boolean,
    default: true,
  },
  openMenuId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits([
  'click',
  'action',
  'menu-open',
  'menu-close',
  'select',
  'hover',
]);

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const deleteDialogRef = ref(null);

// Checkbox 双向绑定
const modelValue = computed({
  get: () => props.isSelected,
  set: value => emit('select', value),
});

// 根据父组件传入的 openMenuId 判断当前菜单是否打开
const showActionsDropdown = computed(
  () => props.openMenuId === props.assistant.id
);

// 下拉菜单展开方向：'down' 或 'up'
const dropdownDirection = ref('down');
const triggerRef = ref(null);

// 内联编辑名称相关
const isEditingName = ref(false);
const editingName = ref('');
const nameInputRef = ref(null);

// 开始编辑名称
const startEditingName = () => {
  editingName.value = props.assistant.name || '';
  isEditingName.value = true;
  nextTick(() => {
    if (nameInputRef.value) {
      nameInputRef.value.focus();
      nameInputRef.value.select();
    }
  });
};

// 取消编辑名称
const cancelEditingName = () => {
  isEditingName.value = false;
  editingName.value = '';
};

// 保存名称
const saveName = async () => {
  if (!editingName.value.trim()) {
    cancelEditingName();
    return;
  }

  if (editingName.value !== props.assistant.name) {
    try {
      await store.dispatch('pilotAssistants/update', {
        id: props.assistant.id,
        data: { name: editingName.value.trim() },
      });
      useAlert(t('PILOT.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
    } catch (error) {
      const errorMessage =
        error?.message || t('PILOT.ASSISTANTS.EDIT.ERROR_MESSAGE');
      useAlert(errorMessage);
      cancelEditingName();
      return;
    }
  }

  // 等待一下确保状态更新完成
  await nextTick();
  isEditingName.value = false;
};

// 处理按键事件
const handleNameKeydown = event => {
  if (event.key === 'Enter') {
    saveName();
  } else if (event.key === 'Escape') {
    cancelEditingName();
  }
};

// 检测下拉菜单应该往上还是往下展开
const checkDropdownDirection = () => {
  nextTick(() => {
    const trigger = triggerRef.value;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const dropdownHeight = 200; // 估算下拉菜单高度

    // 如果下方空间不足（小于菜单高度 + 20px 缓冲），就往上展开
    const spaceBelow = windowHeight - triggerRect.bottom;
    dropdownDirection.value = spaceBelow < dropdownHeight + 20 ? 'up' : 'down';
  });
};

const handleClick = () => {
  if (showActionsDropdown.value) {
    emit('menu-close');
    return;
  }
  emit('click', props.assistant);
};

const handleMenuAction = async ({ action }) => {
  emit('menu-close');

  if (action === 'view_inboxes') {
    // 跳转到关联的收件箱页面
    router.push({
      name: 'pilot_assistants_inboxes_index',
      params: {
        accountId: router.currentRoute.value.params.accountId,
        assistantId: props.assistant.id,
      },
    });
  } else if (action === 'config') {
    // 跳转到配置页面
    router.push({
      name: 'pilot_assistant_config',
      params: {
        accountId: router.currentRoute.value.params.accountId,
        assistantId: props.assistant.id,
      },
    });
  } else if (action === 'publish' || action === 'unpublish') {
    await handleStatusToggle(action);
  } else if (action === 'edit') {
    emit('click', props.assistant);
  } else if (action === 'delete') {
    deleteDialogRef.value.dialogRef.open();
  }

  // 发出 action 事件给父组件处理
  emit('action', { action, id: props.assistant.id });
};

// 切换状态（发布/下架）
const handleStatusToggle = async action => {
  const newStatus = action === 'publish' ? 'published' : 'draft';
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { status: newStatus },
    });
    const successMessage =
      action === 'publish'
        ? t('PILOT.ASSISTANTS.API.PUBLISH.SUCCESS')
        : t('PILOT.ASSISTANTS.API.UNPUBLISH.SUCCESS');
    useAlert(successMessage);
  } catch (error) {
    const errorMessage =
      action === 'publish'
        ? t('PILOT.ASSISTANTS.API.PUBLISH.ERROR')
        : t('PILOT.ASSISTANTS.API.UNPUBLISH.ERROR');
    useAlert(errorMessage);
  }
};

const handleDeleteSuccess = () => {
  router.go(0);
};

const toggleDropdown = event => {
  event.stopPropagation();
  if (showActionsDropdown.value) {
    // 关闭菜单
    emit('menu-close');
  } else {
    // 打开菜单
    emit('menu-open', props.assistant.id);
    // 打开后检测方向
    checkDropdownDirection();
  }
};

// 点击外部关闭 dropdown
const handleClickOutside = () => {
  if (showActionsDropdown.value) {
    emit('menu-close');
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const menuItems = computed(() => {
  const items = [
    {
      label: t('PILOT.ASSISTANTS.OPTIONS.VIEW_CONNECTED_INBOXES'),
      value: 'view_inboxes',
      action: 'view_inboxes',
      icon: 'i-lucide-inbox',
    },
  ];

  // 根据状态添加发布/下架选项
  if (props.assistant.status === 'draft') {
    items.push({
      label: t('PILOT.ASSISTANTS.OPTIONS.PUBLISH'),
      value: 'publish',
      action: 'publish',
      icon: 'i-lucide-upload-cloud',
    });
  } else {
    items.push({
      label: t('PILOT.ASSISTANTS.OPTIONS.UNPUBLISH'),
      value: 'unpublish',
      action: 'unpublish',
      icon: 'i-lucide-download-cloud',
    });
  }

  items.push(
    {
      label: t('PILOT.ASSISTANTS.OPTIONS.EDIT_ASSISTANT'),
      value: 'edit',
      action: 'edit',
      icon: 'i-lucide-pencil',
    },
    {
      label: t('PILOT.ASSISTANTS.OPTIONS.CONFIG_ASSISTANT'),
      value: 'config',
      action: 'config',
      icon: 'i-lucide-settings',
    },
    {
      label: t('PILOT.ASSISTANTS.OPTIONS.DELETE_ASSISTANT'),
      value: 'delete',
      action: 'delete',
      icon: 'i-lucide-trash-2',
    }
  );

  return items;
});

const getInitial = name => {
  return name.charAt(0).toUpperCase();
};

const getColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const getCreatorColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

// 状态配置 - 根据助手状态动态显示
const statusConfig = computed(() => {
  const configs = {
    draft: {
      label: t('PILOT.ASSISTANTS.STATUS.DRAFT'),
      bgColor: 'bg-n-amber-1 dark:bg-n-amber-2',
      textColor: 'text-n-amber-10 dark:text-n-amber-9',
      dotColor: 'bg-n-amber-5',
    },
    published: {
      label: t('PILOT.ASSISTANTS.STATUS.PUBLISHED'),
      bgColor: 'bg-n-blue-1 dark:bg-n-blue-2',
      textColor: 'text-n-blue-10 dark:text-n-blue-9',
      dotColor: 'bg-n-blue-5',
    },
  };
  return configs[props.assistant.status] || configs.draft;
});

// 判断是否有 avatar
const hasAvatar = computed(() => !!props.assistant.avatar_url);
</script>

<template>
  <div
    class="relative group"
    @mouseenter="$emit('hover', true)"
    @mouseleave="$emit('hover', false)"
  >
    <!-- 下拉菜单 - 移到最外层避免被裁切 -->
    <DropdownMenu
      v-if="showMenu && showActionsDropdown"
      :menu-items="menuItems"
      class="absolute top-12 right-3 z-50"
      @action="handleMenuAction($event)"
    />

    <div
      class="group bg-n-slate-white dark:bg-n-slate-800 rounded-xl cursor-pointer"
      style="min-height: 200px"
      @click="handleClick"
    >
      <!-- Checkbox (批量选择) - 独立在左上角 -->
      <div
        v-show="selectable"
        class="absolute top-4 ltr:left-3 rtl:right-3 z-10"
      >
        <div class="scale-150 origin-left">
          <Checkbox v-model="modelValue" @click.stop />
        </div>
      </div>

      <div class="flex flex-col h-full">
        <!-- 上半部分：logo、描述、状态 - 独立的圆角卡片 -->
        <div
          class="relative bg-[#F4F5F6] group-hover:bg-[#E3E5E8] rounded-xl p-4 transition-colors duration-200"
        >
          <div
            class="bg-contain bg-center rounded-xl transition-all duration-200 group-hover:opacity-90 relative bg-no-repeat overflow-hidden"
            :style="{
              backgroundImage: `url(${assistantsHeaderImg})`,
              aspectRatio: '4/3',
            }"
          >
            <div class="absolute inset-0 p-4 flex flex-col">
              <!-- 操作按钮（右上角绝对定位） -->
              <div
                ref="triggerRef"
                v-on-clickaway="closeDropdown"
                class="absolute top-3 right-3 flex items-center z-10"
                @click.stop
              >
                <!-- 下拉菜单按钮 - 在可显示菜单时显示 -->
                <Button
                  v-if="showMenu && !showActionsDropdown"
                  icon="i-lucide-ellipsis-vertical"
                  color="slate"
                  size="sm"
                  class="rounded-md opacity-0 group-hover:opacity-100 transition-opacity !bg-white dark:!bg-n-slate-700 shadow-sm hover:!bg-n-alpha-3"
                  @click="toggleDropdown"
                />
              </div>

              <!-- Top Section: Logo (Left) + Description (Right) -->
              <div class="flex items-start gap-2">
                <!-- Left: Logo Container -->
                <div class="flex-shrink-0">
                  <img
                    v-if="hasAvatar"
                    :src="assistant.avatar_url"
                    :alt="assistant.name"
                    class="w-14 h-14 rounded-lg object-cover shadow-lg"
                  />
                  <div
                    v-else
                    class="w-14 h-14 rounded-lg flex items-center justify-center shadow-lg"
                    :style="{
                      backgroundColor: getColor(assistant.name || 'Assistant'),
                    }"
                  >
                    <span class="text-xl font-semibold text-white">
                      {{ getInitial(assistant.name || 'Assistant') }}
                    </span>
                  </div>
                </div>

                <!-- Right: Chat Bubble Style Description -->
                <div class="flex-1 min-w-0">
                  <!-- 气泡内容 -->
                  <div
                    class="relative bg-white dark:bg-n-slate-700 rounded-2xl px-4 py-3 shadow-sm ml-3"
                  >
                    <!-- 小三角指向头像 - 紧贴气泡左边缘 -->
                    <div
                      class="absolute left-0 top-4 -translate-x-full w-0 h-0 border-y-[6px] border-y-transparent border-r-[8px] border-r-white dark:border-r-n-slate-700"
                    />
                    <p
                      v-if="assistant.description"
                      class="text-sm text-n-slate-12 dark:text-n-slate-3 line-clamp-3 break-words overflow-hidden"
                    >
                      {{ assistant.description }}
                    </p>
                    <p
                      v-else
                      class="text-sm text-n-slate-10 dark:text-n-slate-5 italic"
                    >
                      {{ t('PILOT.ASSISTANTS.NO_DESCRIPTION') }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- 状态标签 -->
              <div class="mt-2">
                <div
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-n-slate-12 drop-shadow-md"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="[statusConfig.dotColor]"
                  />
                  <span>{{ statusConfig.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部区域：名称、创建人、时间、统计信息 -->
        <div
          class="flex flex-col px-0 pb-0 pt-1 transition-all duration-200 relative"
        >
          <!-- 第一行：名称 -->
          <div class="group/name">
            <!-- 默认显示：名称 + 编辑图标 -->
            <div v-if="!isEditingName" class="flex items-center gap-1">
              <h3
                class="text-[14px] font-semibold text-n-slate-12 truncate cursor-pointer hover:text-n-slate-11"
                @click.stop="startEditingName"
              >
                {{ assistant.name || 'Unnamed Assistant' }}
              </h3>
              <!-- 编辑图标 - hover时显示 -->
              <button
                class="opacity-0 group-hover/name:opacity-100 transition-opacity flex-shrink-0"
                @click.stop="startEditingName"
              >
                <div
                  class="i-lucide-pencil text-[14px] text-n-slate-10 hover:text-n-slate-12"
                />
              </button>
            </div>

            <!-- 编辑模式：输入框 -->
            <div v-else class="flex items-center gap-1" @click.stop>
              <input
                ref="nameInputRef"
                v-model="editingName"
                type="text"
                class="w-full text-[14px] font-semibold text-n-slate-12 bg-white dark:bg-n-slate-700 border border-n-blue-5 rounded px-2 py-1 outline-none focus:border-n-blue-6"
                @keydown="handleNameKeydown"
                @blur="saveName"
              />
            </div>
          </div>

          <!-- 第二行：创建人 + 更新时间 -->
          <div class="flex items-center justify-between">
            <!-- Left: 创建人圆圈 + 名称 -->
            <div class="flex items-center gap-2">
              <div
                class="w-4 h-4 rounded-full flex items-center justify-center"
                :style="{
                  backgroundColor: getCreatorColor(
                    assistant.creator?.name || 'Unknown'
                  ),
                }"
              >
                <span class="text-[10px] font-semibold" style="color: #ffffff">
                  {{ getInitial(assistant.creator?.name || 'Unknown') }}
                </span>
              </div>
              <span class="text-xs text-n-slate-10">
                {{ assistant.creator?.name || 'Unknown' }}
              </span>
            </div>

            <!-- Right: 更新时间 -->
            <div>
              <span class="text-xs text-n-slate-10">
                {{
                  new Date(assistant.updated_at)
                    .toLocaleString('zh-CN', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                    .replace(/\//g, '-')
                }}
              </span>
            </div>
          </div>

          <!-- 第三行：统计信息（知识库数） -->
          <div class="flex items-center gap-1.5 text-xs text-n-slate-11">
            <i class="i-lucide-book-open text-sm" />
            <span>{{ assistant.knowledge_bases_count || 0 }}
              {{ t('PILOT.ASSISTANTS.KNOWLEDGE_BASES') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Dialog -->
    <DeleteDialog
      ref="deleteDialogRef"
      :entity="{
        id: assistant.id,
        name: assistant.name || 'Unnamed Assistant',
      }"
      type="Assistant"
      translation-key="ASSISTANTS"
      @delete-success="handleDeleteSuccess"
    />
  </div>
</template>
